import type { FC, ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { SplitReveal } from '@/components/generals/split-reveal'
import { TransitionLink } from '@/components/generals/transition-link'
import { ContactForm } from '@/components/subpages/contact-form'

import {
    BOOKING_HREF,
    contact,
    openingHours
} from '@/constants/site'

/** The first chapter of the contact page: how to reach us, beside the form. */
export const ContactSection: FC = (): ReactNode => {
    return (
        <section
            id='write-to-us'
            aria-labelledby='write-to-us-heading'
            className='relative z-10 -mt-px rounded-t-[1.75rem] bg-bone text-charcoal shadow-[0_-30px_70px_-20px_rgba(9,17,12,0.55)] sm:rounded-t-[2.5rem]'
        >
            <div className='grid gap-16 px-5 pb-24 pt-20 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-32 lg:pt-28'>
                <div className='lg:col-span-5'>
                    <p className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-charcoal/70'>
                        <span className='tabular-nums'>
                            01
                        </span>

                        <span aria-hidden='true' className='h-px w-8 bg-current opacity-40' />

                        <span>
                            Get in touch
                        </span>
                    </p>

                    <SplitReveal
                        as='h2'
                        id='write-to-us-heading'
                        className='mt-6 text-[clamp(2.2rem,4.2vw,4.2rem)] font-light leading-[1.02] tracking-[-0.03em]'
                    >
                        Every message is read by a person, <em className='font-serif font-normal'>not a queue.</em>
                    </SplitReveal>

                    <p data-reveal className='mt-6 max-w-md text-base leading-relaxed text-charcoal/70'>
                        Membership questions, a wedding on the cliff, a lost umbrella from last Tuesday. Whatever it is, the concierge team will reply within one working day.
                    </p>

                    <dl className='mt-12 border-t border-charcoal/15'>
                        <div data-reveal className='grid gap-2 border-b border-charcoal/15 py-5 sm:grid-cols-[9rem_1fr]'>
                            <dt className='font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal/55'>
                                Concierge
                            </dt>

                            <dd>
                                <a href={`mailto:${contact.email}`} className='break-all underline decoration-charcoal/25 underline-offset-4 transition-colors hover:text-forest'>
                                    {contact.email}
                                </a>
                            </dd>
                        </div>

                        <div data-reveal className='grid gap-2 border-b border-charcoal/15 py-5 sm:grid-cols-[9rem_1fr]'>
                            <dt className='font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal/55'>
                                Address
                            </dt>

                            <dd>
                                <address className='not-italic leading-relaxed'>
                                    {contact.addressLines.map((line) => (
                                        <span key={line} className='block'>
                                            {line}
                                        </span>
                                    ))}
                                </address>
                            </dd>
                        </div>

                        <div data-reveal className='grid gap-2 border-b border-charcoal/15 py-5 sm:grid-cols-[9rem_1fr]'>
                            <dt className='font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal/55'>
                                Hours
                            </dt>

                            <dd className='space-y-1'>
                                {openingHours.map((item) => (
                                    <span key={item.label} className='flex justify-between gap-6'>
                                        <span>
                                            {item.label}
                                        </span>

                                        <span className='text-charcoal/55'>
                                            {item.value}
                                        </span>
                                    </span>
                                ))}
                            </dd>
                        </div>

                        <div data-reveal className='grid gap-2 border-b border-charcoal/15 py-5 sm:grid-cols-[9rem_1fr]'>
                            <dt className='font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal/55'>
                                Replies
                            </dt>

                            <dd>
                                Within one working day, in English, Portuguese or Spanish.
                            </dd>
                        </div>
                    </dl>

                    <TransitionLink
                        href={BOOKING_HREF}
                        data-reveal
                        className='mt-10 inline-flex h-12 items-center gap-3 rounded-full border border-charcoal/20 px-6 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors hover:border-charcoal hover:bg-charcoal hover:text-bone'
                    >
                        Request a booking instead
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </TransitionLink>
                </div>

                <div className='lg:col-span-7 lg:pl-6'>
                    <div data-reveal className='relative rounded-[8px] border border-charcoal/10 bg-paper p-6 shadow-[0_60px_120px_-70px_rgba(27,27,25,0.6)] sm:p-10 lg:p-12'>
                        <ContactForm />
                    </div>
                </div>
            </div>
        </section>
    )
}
